import "./style.css";
import { initializeProviderOnboarding } from "./onboarding";
import { initializeProductConsole } from "./product-console";
import { initializeConnectorConsole } from "./connector-console";
import { initializeEmailConsole } from "./email-console";
import { initializeOrderConsole } from "./order-console";
import { escapeHTML, formatUSDC, shortValue, showStatus } from "./ui-utils";

type Provider = {
  id: string;
  name: string;
  slug: string;
  status: string;
};

type ProviderUser = {
  id: string;
  providerId: string;
  name: string;
  email: string;
  role: string;
};

type FeeRule = {
  id: string;
  scope: "DEFAULT" | "PROVIDER";
  providerId?: string;
  feeBps: number;
  version: number;
};

type FinanceSummary = {
  usageCount: number;
  buyerGrossAtomic: string;
  platformFeeAtomic: string;
  providerPayableAtomic: string;
  providerPaidAtomic: string;
  pendingPayouts: number;
};

type ProviderPayable = {
  id: string;
  providerRef: string;
  amountAtomic: string;
  paidAmountAtomic: string;
  fundingState: string;
  status: string;
  createdAt: string;
};

type Payout = {
  id: string;
  status: string;
  amountAtomic: string;
  transactionHash?: string;
  lastErrorCode?: string;
  createdAt: string;
};

type View = "products" | "orders" | "connectors" | "email" | "finance";

const apiBase = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8080";
const providerAPIDocsURL = `${apiBase.replace(/\/+$/, "")}/provider-integration/docs`;

document.querySelectorAll<HTMLAnchorElement>("[data-provider-api-link]").forEach(link => {
  link.href = providerAPIDocsURL;
});

let token = sessionStorage.getItem("bizx402-provider-token") || "";
let provider: Provider | undefined;
let currentUser: ProviderUser | undefined;
let feeRule: FeeRule | undefined;

const loginView = document.querySelector<HTMLElement>("#login-view")!;
const appView = document.querySelector<HTMLElement>("#app-view")!;
const loginForm = document.querySelector<HTMLFormElement>("#login-form")!;

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${apiBase}${path}`, { ...options, headers });
  const body = await response.json().catch(() => ({})) as {
    error?: { message?: string };
  };
  if (!response.ok) {
    if (response.status === 401 && path !== "/v1/provider/login") showLogin();
    throw new Error(body.error?.message || `请求失败：HTTP ${response.status}`);
  }
  return body as T;
}

const productConsole = initializeProductConsole(api);
const connectorConsole = initializeConnectorConsole(api);
const emailConsole = initializeEmailConsole(api);
const orderConsole = initializeOrderConsole(api);

function showLogin(): void {
  token = "";
  provider = undefined;
  currentUser = undefined;
  feeRule = undefined;
  sessionStorage.removeItem("bizx402-provider-token");
  loginView.hidden = false;
  appView.hidden = true;
}

function showApp(): void {
  loginView.hidden = true;
  appView.hidden = false;
  document.querySelector("#provider-name")!.textContent = provider?.name || "—";
  document.querySelector("#current-user")!.textContent = currentUser
    ? `${currentUser.name} · ${currentUser.role}`
    : "—";
  if (feeRule) productConsole.setFeeRule(feeRule);
  renderView(viewFromLocation());
}

function viewFromLocation(): View {
  if (window.location.hash === "#/finance") return "finance";
  if (window.location.hash === "#/connectors") return "connectors";
  if (window.location.hash === "#/email") return "email";
  if (window.location.hash === "#/orders") return "orders";
  return "products";
}

function renderView(view: View): void {
  if (window.location.hash !== `#/${view}`) window.history.replaceState(null, "", `#/${view}`);
  document.querySelectorAll<HTMLButtonElement>(".nav-item").forEach(button => {
    button.classList.toggle("active", button.dataset.view === view);
  });
  document.querySelector<HTMLElement>("#products-view")!.hidden = view !== "products";
  document.querySelector<HTMLElement>("#orders-view")!.hidden = view !== "orders";
  document.querySelector<HTMLElement>("#connectors-view")!.hidden = view !== "connectors";
  document.querySelector<HTMLElement>("#email-view")!.hidden = view !== "email";
  document.querySelector<HTMLElement>("#finance-view")!.hidden = view !== "finance";
  document.querySelector("#view-title")!.textContent = view === "products" ? "服务管理" : view === "orders" ? "订单列表" : view === "connectors" ? "Connector 接入" : view === "email" ? "邮件通知" : "收入与出款";
  document.querySelector<HTMLElement>("#new-product")!.hidden = view !== "products";
  if (view === "products") void productConsole.loadProducts();
  if (view === "orders") void orderConsole.load();
  if (view === "connectors") void connectorConsole.load();
  if (view === "email") void emailConsole.load();
  if (view === "finance") void loadFinance();
}

async function loadFinance(): Promise<void> {
  try {
    const body = await api<{
      summary: FinanceSummary;
      payables: ProviderPayable[];
      payouts: Payout[];
    }>("/v1/provider/finance?limit=100");
    document.querySelector("#summary-count")!.textContent = `${body.summary.pendingPayouts} PENDING`;
    document.querySelector("#finance-usage")!.textContent = String(body.summary.usageCount);
    document.querySelector("#finance-gross")!.textContent = formatUSDC(body.summary.buyerGrossAtomic);
    document.querySelector("#finance-fee")!.textContent = formatUSDC(body.summary.platformFeeAtomic);
    document.querySelector("#finance-payable")!.textContent = formatUSDC(body.summary.providerPayableAtomic);
    document.querySelector("#finance-paid")!.textContent = formatUSDC(body.summary.providerPaidAtomic);
    document.querySelector("#finance-pending")!.textContent = String(body.summary.pendingPayouts);
    const payableRows = document.querySelector<HTMLElement>("#payable-rows")!;
    payableRows.innerHTML = body.payables.map(item => `
      <tr><td><strong>${escapeHTML(shortValue(item.id))}</strong><small>${escapeHTML(item.providerRef)}</small></td><td>${escapeHTML(formatUSDC(item.amountAtomic))} USDC</td><td><span class="status-chip">${escapeHTML(item.fundingState)}</span></td><td><span class="status-chip">${escapeHTML(item.status)}</span></td></tr>
    `).join("");
    document.querySelector<HTMLElement>("#payable-empty")!.hidden = body.payables.length !== 0;
    const payoutRows = document.querySelector<HTMLElement>("#payout-rows")!;
    payoutRows.innerHTML = body.payouts.map(item => `
      <tr><td><strong>${escapeHTML(shortValue(item.id))}</strong><small>${escapeHTML(new Date(item.createdAt).toLocaleString("zh-CN"))}</small></td><td>${escapeHTML(formatUSDC(item.amountAtomic))} USDC</td><td><span class="status-chip">${escapeHTML(item.status)}</span></td><td><code>${escapeHTML(item.transactionHash ? shortValue(item.transactionHash) : item.lastErrorCode || "—")}</code></td></tr>
    `).join("");
    document.querySelector<HTMLElement>("#payout-empty")!.hidden = body.payouts.length !== 0;
  } catch (error) {
    document.querySelector("#summary-count")!.textContent = error instanceof Error ? error.message : "加载失败";
  }
}

loginForm.addEventListener("submit", async event => {
  event.preventDefault();
  try {
    const body = await api<{ token: string; user: ProviderUser; provider: Provider; feeRule: FeeRule }>(
      "/v1/provider/login",
      {
        method: "POST",
        body: JSON.stringify({
          email: (document.querySelector<HTMLInputElement>("#login-email")!).value.trim(),
          password: (document.querySelector<HTMLInputElement>("#login-password")!).value,
        }),
      },
    );
    token = body.token;
    currentUser = body.user;
    provider = body.provider;
    feeRule = body.feeRule;
    sessionStorage.setItem("bizx402-provider-token", token);
    showApp();
  } catch (error) {
    showStatus("login-status", error instanceof Error ? error.message : "登录失败", true);
  }
});

document.querySelectorAll<HTMLButtonElement>(".nav-item").forEach(button => {
  button.addEventListener("click", () => {
    window.location.hash = `#/${button.dataset.view}`;
  });
});
document.querySelector("#logout-button")!.addEventListener("click", showLogin);
window.addEventListener("hashchange", () => renderView(viewFromLocation()));

async function restore(): Promise<void> {
  if (!token) {
    showLogin();
    return;
  }
  try {
    const body = await api<{ user: ProviderUser; provider: Provider; feeRule: FeeRule }>("/v1/provider/me");
    currentUser = body.user;
    provider = body.provider;
    feeRule = body.feeRule;
    showApp();
  } catch {
    showLogin();
  }
}

initializeProviderOnboarding({ apiBase, showProviderLogin: showLogin });
void restore();
